import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals, hexKey } from "../hex";
import { buildStructure, nextImprovement } from "../land/build";
import { inCityReach, isWorkingCityOf } from "../land/cities";
import { canTake } from "../land/territory";
import { applyFirst, nearestTo, rankBy } from "../pick";
import {
  findHex,
  isOwnGround,
  isPlainOwnHex,
  patchHexAt,
  unitAt,
  unitById,
  unitsOf,
} from "../run/board";
import { onCadence } from "../run/cadence";
import { kingOf, spend, supplyOf } from "../run/pools";
import { scriptUnitsOf } from "../run/seats";
import { hostile, sameOwner } from "../run/sides";
import type { FactionId, HexState, MayorStance, RunState, Unit, UnitKind } from "../types/state";
import { buildUnit, unitCount, unitPrice } from "../units/roster";
import { isOrderLocked, orderUnit, routeUnit } from "../units/routes";
import { replaceUnit, restedFor } from "../units/unit";
import { isRushed } from "./general";
import { commandRefusal, mayoredCities, spendWill, trust } from "./king";

/**
 * Mayors and lords (ADR-0011 slice 2, A32, A45): the governor the King sends, a city's stance, and the
 * mayor's turn (train, develop, muster and commission).
 */

/** An own working city with no mayor in office and no soldier already on its way to the office. */
export function awaitsGovernor(state: RunState, faction: FactionId, coord: AxialCoord): boolean {
  const hex = findHex(state, coord);
  return (
    isWorkingCityOf(hex, faction, state.tick) &&
    !hex.mayor &&
    !state.units.some((unit) => unit.governs && hexEquals(unit.governs, coord))
  );
}

/** The govern verb's precondition for the soldier alone: the King's to command, and the court's leave. */
export function canGovern(state: RunState, faction: FactionId, unitId: number): boolean {
  const unit = unitById(state, unitId);
  return (
    state.status === "running" &&
    state.kings !== null &&
    unit !== undefined &&
    unit.owner === faction &&
    !unit.commander &&
    !isOrderLocked(state, unit) &&
    commandRefusal(state, faction, unit.coord, "appoint") === undefined
  );
}

/**
 * The King sends a soldier within his reach to govern one of his cities (A32): `costs.appoint`
 * Will at once, never refunded. The soldier walks there as under any order; when it stands in
 * the city, or beside it while an own soldier holds the city hex, it leaves the map and the
 * city has its mayor, who holds a pip of Trust while in office. A squad soldier leaves its
 * squad. Same object back when refused.
 */
export function appointGovernor(
  state: RunState,
  faction: FactionId,
  unitId: number,
  city: AxialCoord,
): RunState {
  const unit = unitById(state, unitId);
  if (!unit || !canGovern(state, faction, unitId) || !awaitsGovernor(state, faction, city)) {
    return state;
  }
  const paid = spendWill(state, faction, "appoint");
  if (hexEquals(unit.coord, city)) {
    return patchHexAt(
      { ...paid, units: state.units.filter((candidate) => candidate !== unit) },
      city,
      { mayor: true },
    );
  }
  if (!routeUnit(state, unit, city)) {
    return state;
  }
  return replaceUnit(paid, unit, {
    ...without(unit, "squadOf"),
    destination: city,
    governs: city,
    orderedTick: state.tick,
    orderedBy: "king",
  });
}

/** The King takes the office back, for no Will; the pip returns. Same object back when refused. */
export function dismissMayor(state: RunState, faction: FactionId, coord: AxialCoord): RunState {
  const hex = findHex(state, coord);
  const allowed =
    state.status === "running" &&
    hex !== undefined &&
    hex.owner === faction &&
    hex.mayor &&
    commandRefusal(state, faction, coord, "dismiss") === undefined;
  return allowed ? patchHexAt(state, coord, { mayor: false }) : state;
}

/** A mayored city's stance (A45): garrison until the King says otherwise; undefined without a mayor. */
export function stanceOf(state: RunState, coord: AxialCoord): MayorStance | undefined {
  const hex = findHex(state, coord);
  return hex?.mayor ? (hex.stance ?? "garrison") : undefined;
}

/** The living leader this city's lord commissioned (A46): one lord, one banner. */
export function lordsLeader(state: RunState, coord: AxialCoord): Unit | undefined {
  const hex = findHex(state, coord);
  if (!hex?.mayor || hex.leader === undefined) {
    return undefined;
  }
  const leader = unitById(state, hex.leader);
  return leader?.commander && leader.owner === hex.owner ? leader : undefined;
}

/**
 * The King gives a mayor its stance (A45): a court command (kingdom-wide from the capital, else
 * with the city in reach), `costs.stance` Will. The stance it already has is a no-op; same
 * object back when refused.
 */
export function setStance(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
  stance: MayorStance,
): RunState {
  const hex = findHex(state, coord);
  if (
    state.status !== "running" ||
    !hex?.mayor ||
    !sameOwner(hex.owner, faction) ||
    stanceOf(state, coord) === stance ||
    commandRefusal(state, faction, coord, "stance") !== undefined
  ) {
    return state;
  }
  return patchHexAt(spendWill(state, faction, "stance"), coord, { stance });
}

/**
 * The mayor whose land a hex is: the nearest of the faction's mayored cities within
 * `cityReach`, ties to insertion order; undefined when no mayor covers it.
 */
export function mayorOf(
  state: RunState,
  faction: FactionId,
  coord: AxialCoord,
): HexState | undefined {
  return nearestTo(
    mayoredCities(state, faction),
    coord,
    (city) => hexDistance(city.coord, coord) <= state.rules.cityReach,
  );
}

/** Neutral, wall-free, unrazed land of this mayor's that no own unit stands on or walks to. */
export function mayorLand(state: RunState, faction: FactionId, city: HexState): HexState[] {
  const taken = new Set<string>();
  for (const unit of unitsOf(state, faction)) {
    taken.add(hexKey(unit.coord));
    if (unit.destination) {
      taken.add(hexKey(unit.destination));
    }
  }
  // By coord, never by object: a yard that has just started a unit is a new hex value.
  const isCitys = (coord: AxialCoord): boolean => {
    const mayor = mayorOf(state, faction, coord);
    return mayor !== undefined && hexEquals(mayor.coord, city.coord);
  };
  return state.hexes.filter(
    (hex) =>
      hex.owner === "none" &&
      !hex.razed &&
      hex.walls === 0 &&
      !taken.has(hexKey(hex.coord)) &&
      isCitys(hex.coord),
  );
}

/**
 * A46: a mustering lord with no living leader, `musterSize` free soldiers standing within one
 * hex of its city, a pip of Trust to hand over and the supply for it (the reserve kept)
 * commissions the healthiest of them (then the lower id). The others join by contact in the
 * move step. No Will: the lord is a delegate, and the supply is the price.
 */
function commission(state: RunState, faction: FactionId, city: HexState): RunState {
  const court = state.rules.court;
  if (!court || lordsLeader(state, city.coord) || trust(state, faction) <= 1) {
    return state;
  }
  if (supplyOf(state, faction) - court.lords.commission <= court.mayorReserve) {
    return state;
  }
  const free = scriptUnitsOf(state, faction).filter(
    (unit) =>
      !unit.commander &&
      unit.squadOf === undefined &&
      !unit.governs &&
      !unit.destination &&
      hexDistance(unit.coord, city.coord) <= 1,
  );
  const [leader] = rankBy(
    free,
    (unit) => -unit.hp,
    (unit) => unit.id,
  );
  if (!leader || free.length < court.lords.musterSize) {
    return state;
  }
  const paid = spend(
    replaceUnit(state, leader, { ...leader, commander: true }),
    faction,
    court.lords.commission,
  );
  return patchHexAt(paid, city.coord, { leader: leader.id });
}

/** Own plain hexes of this mayor's land that could take an improvement, price aside (A54). */
export function developSites(state: RunState, faction: FactionId, city: HexState): HexState[] {
  if (!state.rules.improvements) {
    return [];
  }
  return state.hexes.filter(
    (hex) =>
      isPlainOwnHex(hex, faction) &&
      inCityReach(state, hex.coord) &&
      mayorOf(state, faction, hex.coord)?.coord === city.coord,
  );
}

/**
 * A54: a mayor told to develop buys one improvement a turn on the nearest plain hex of his
 * land (insertion-order ties), while the pool keeps more than `mayorReserve` after the price.
 * A farm and a hamlet in turn, counted over his land so a capture is made up for; farms only
 * without the population block, where a hamlet would grow nothing. No Will: a delegate's.
 */
function develop(state: RunState, faction: FactionId, city: HexState): RunState {
  const court = state.rules.court;
  const improvements = state.rules.improvements;
  if (!court || !improvements) {
    return state;
  }
  if (supplyOf(state, faction) - improvements.cost <= court.mayorReserve) {
    return state;
  }
  const mine = state.hexes.filter(
    (hex) => isOwnGround(hex, faction) && mayorOf(state, faction, hex.coord)?.coord === city.coord,
  );
  const kind = nextImprovement(state, mine);
  const [site] = rankBy(developSites(state, faction, city), (hex) =>
    hexDistance(hex.coord, city.coord),
  );
  return site ? buildStructure(state, faction, site.coord, kind, "mayor") : state;
}

/**
 * One mayor's turn (ADR-0011 slice 2), deliberately mediocre and always the same. Since A36
 * it governs inside the King's reach as well; only the yard is the King's while he stands in
 * the city itself. Thrifty: it trains Sword and Spear in turn, and only while
 * the pool keeps more than `mayorReserve` after the price. Defensive: with an enemy unit on
 * its land every idle soldier farther than one hex from the city is called to the nearest
 * free own hex beside it; otherwise an idle soldier goes to the nearest neutral, wall-free
 * hex of its land. Deferential: it touches only a soldier that has neither moved nor been
 * ordered for a whole cadence, and never a commander or a squad member. It never enters enemy
 * land, founds, fortifies or razes, and it spends no Will: its commands go out as a delegate's.
 * Told to develop (A54) it trains nobody and buys an improvement a turn instead, under the
 * King's eyes as well: the yard is his, the land is the mayor's.
 */
function mayorTurn(state: RunState, faction: FactionId, at: AxialCoord): RunState {
  const court = state.rules.court;
  const city = findHex(state, at);
  if (!court || !city?.mayor || !sameOwner(city.owner, faction)) {
    return state;
  }
  const governs = (coord: AxialCoord, from: RunState): boolean =>
    mayorOf(from, faction, coord)?.coord === city.coord;
  const king = kingOf(state, faction);
  const kingInResidence = king !== undefined && hexEquals(king.coord, city.coord);
  const stance = stanceOf(state, at);
  let next =
    stance === "muster"
      ? commission(state, faction, city)
      : stance === "develop"
        ? develop(state, faction, city)
        : state;
  // A mustering lord holds its soldiers by the city until it has named their leader (A46).
  const mustering = stance === "muster" && lordsLeader(next, at) === undefined;
  if (!kingInResidence && stance !== "save" && stance !== "develop") {
    const kind: UnitKind = unitCount(next, faction) % 2 === 0 ? "sword" : "spear";
    // A rushed city's mayor spends below his reserve: the King's purse is open there (A34).
    const reserve = isRushed(next, faction, city.coord) ? -1 : court.mayorReserve;
    if (supplyOf(next, faction) - unitPrice(next, faction, kind) > reserve) {
      next = buildUnit(next, faction, city.coord, kind, "mayor");
    }
  }
  const threatened = next.units.some(
    (unit) =>
      hostile(next.rules, unit.owner, faction) &&
      mayorOf(next, faction, unit.coord)?.coord === city.coord,
  );
  for (const unit of scriptUnitsOf(next, faction)) {
    const rested = restedFor(next, unit, court.mayorCadenceTicks);
    const taking = canTake(next, faction, unit.coord);
    if (unit.commander || unit.squadOf !== undefined) {
      continue;
    }
    if (unit.destination || !rested || !governs(unit.coord, next) || (taking && !threatened)) {
      continue;
    }
    const homeward = threatened || mustering;
    if (homeward && hexDistance(unit.coord, city.coord) <= 1) {
      continue;
    }
    const current = next;
    const goals = homeward
      ? current.hexes.filter(
          (hex) =>
            isOwnGround(hex, faction) &&
            hexDistance(hex.coord, city.coord) <= 1 &&
            !unitAt(current, hex.coord) &&
            !unitsOf(current, faction).some(
              (other) => other.destination && hexEquals(other.destination, hex.coord),
            ),
        )
      : mayorLand(current, faction, city);
    next = applyFirst(
      next,
      rankBy(goals, (hex) => hexDistance(hex.coord, unit.coord)),
      (from, hex) => orderUnit(from, faction, unit.id, hex.coord, "mayor"),
    );
  }
  return next;
}

/** End of tick, on the mayors' cadence: every mayor of both factions takes its turn, in hex order. */
export function mayorsAct(state: RunState): RunState {
  const court = state.rules.court;
  if (!court || !onCadence(state, court.mayorCadenceTicks)) {
    return state;
  }
  let next = state;
  for (const faction of state.factions) {
    for (const city of mayoredCities(state, faction)) {
      next = mayorTurn(next, faction, city.coord);
    }
  }
  return next;
}
